import type { ActivityMode, CompletedTimer, CompletedTimerRevision, RunningTimer, TagDefinition } from "../domain";
import type { LocalLedger, NudgeEvent, NudgePolicy } from "../ledger";

export interface LedgerSyncState {
  revision: number;
  ledger: LocalLedger | null;
}

export interface LedgerSyncCommand {
  commandId: string;
  deviceId: string;
  baseRevision: number;
  issuedAt: string;
  ledger: LocalLedger;
}

export type LedgerSyncResult = {
  status: "applied" | "duplicate";
  revision: number;
  ledger: LocalLedger;
} | {
  status: "conflict";
  revision: number;
  ledger: LocalLedger;
};

const modes = new Set<ActivityMode>(["interactive_mac", "passive_mac", "away_from_mac", "mixed"]);
const reasons = new Set<NudgeEvent["reason"]>(["overdue", "returned", "mode-contradiction"]);
const presentations = new Set<NudgeEvent["presentation"]>(["inline", "native", "web"]);
const outcomes = new Set<NonNullable<NudgeEvent["outcome"]>>(["continued", "finished", "switched", "snoozed"]);
const identifier = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,199}$/;
const color = /^#[0-9a-f]{6}$/i;
const clock = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const maxSerializedBytes = 750_000;

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function finiteTimestamp(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function string(value: unknown, max: number, pattern?: RegExp): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= max && (!pattern || pattern.test(value));
}

function tagIds(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.length > 32) return null;
  if (!value.every((item) => string(item, 100, identifier))) return null;
  return [...new Set(value as string[])];
}

function timer(value: unknown): RunningTimer | null {
  const item = record(value);
  const description = typeof item?.description === "string" ? item.description.trim().replace(/\s+/g, " ") : "";
  if (!item || !string(item.id, 200, identifier) || !string(description, 180) || !finiteTimestamp(item.startedAt)) return null;
  const tags = tagIds(item.tagIds);
  if (!tags || !modes.has(item.activityMode as ActivityMode)) return null;
  if (item.lastReviewedAt !== undefined && !finiteTimestamp(item.lastReviewedAt)) return null;
  if (item.snoozedUntil !== undefined && !finiteTimestamp(item.snoozedUntil)) return null;
  return {
    id: item.id,
    description,
    startedAt: item.startedAt,
    tagIds: tags,
    activityMode: item.activityMode as ActivityMode,
    ...(item.lastReviewedAt === undefined ? {} : { lastReviewedAt: item.lastReviewedAt as number }),
    ...(item.snoozedUntil === undefined ? {} : { snoozedUntil: item.snoozedUntil as number }),
  };
}

function completedTimer(value: unknown): CompletedTimer | null {
  const running = timer(value);
  const item = record(value);
  if (!running || !item || !finiteTimestamp(item.endedAt) || item.endedAt < running.startedAt) return null;
  if (item.revisions !== undefined && (!Array.isArray(item.revisions) || item.revisions.length > 20)) return null;
  const revisions = (item.revisions as unknown[] | undefined)?.map(completedTimerRevision) ?? [];
  if (revisions.some((revision) => !revision)) return null;
  return { ...running, endedAt: item.endedAt, ...(revisions.length ? { revisions: revisions as CompletedTimerRevision[] } : {}) };
}

function completedTimerRevision(value: unknown): CompletedTimerRevision | null {
  const item = record(value);
  const description = typeof item?.description === "string" ? item.description.trim().replace(/\s+/g, " ") : "";
  if (
    !item || !string(description, 180) || !finiteTimestamp(item.startedAt) ||
    !finiteTimestamp(item.endedAt) || item.endedAt < item.startedAt ||
    !modes.has(item.activityMode as ActivityMode) || !finiteTimestamp(item.supersededAt)
  ) return null;
  const tags = tagIds(item.tagIds);
  return tags ? {
    description,
    startedAt: item.startedAt,
    endedAt: item.endedAt,
    tagIds: tags,
    activityMode: item.activityMode as ActivityMode,
    supersededAt: item.supersededAt,
  } : null;
}

function tag(value: unknown): TagDefinition | null {
  const item = record(value);
  const label = typeof item?.label === "string" ? item.label.trim().replace(/\s+/g, " ") : "";
  if (
    !item || !string(item.id, 100, identifier) || !string(label, 40) ||
    !string(item.color, 7, color) || typeof item.isFavorite !== "boolean" ||
    !modes.has(item.defaultActivityMode as ActivityMode) || !finiteTimestamp(item.createdAt)
  ) return null;
  if (item.archivedAt !== undefined && !finiteTimestamp(item.archivedAt)) return null;
  return {
    id: item.id,
    label,
    color: item.color.toLowerCase(),
    isFavorite: item.isFavorite,
    defaultActivityMode: item.defaultActivityMode as ActivityMode,
    createdAt: item.createdAt,
    ...(item.archivedAt === undefined ? {} : { archivedAt: item.archivedAt as number }),
  };
}

function policy(value: unknown): NudgePolicy | null {
  const item = record(value);
  if (
    !item || typeof item.enabled !== "boolean" ||
    typeof item.reviewAfterMinutes !== "number" || !Number.isInteger(item.reviewAfterMinutes) ||
    item.reviewAfterMinutes < 15 || item.reviewAfterMinutes > 240 ||
    !string(item.quietStart, 5, clock) || !string(item.quietEnd, 5, clock) ||
    typeof item.includeTaskName !== "boolean"
  ) return null;
  return {
    enabled: item.enabled,
    reviewAfterMinutes: item.reviewAfterMinutes,
    quietStart: item.quietStart,
    quietEnd: item.quietEnd,
    includeTaskName: item.includeTaskName,
  };
}

function nudgeEvent(value: unknown): NudgeEvent | null {
  const item = record(value);
  if (
    !item || !string(item.id, 300) || !string(item.timerId, 200, identifier) ||
    !reasons.has(item.reason as NudgeEvent["reason"]) ||
    !presentations.has(item.presentation as NudgeEvent["presentation"]) ||
    !finiteTimestamp(item.occurredAt)
  ) return null;
  if (item.outcome !== undefined && !outcomes.has(item.outcome as NonNullable<NudgeEvent["outcome"]>)) return null;
  return {
    id: item.id,
    timerId: item.timerId,
    reason: item.reason as NudgeEvent["reason"],
    presentation: item.presentation as NudgeEvent["presentation"],
    occurredAt: item.occurredAt,
    ...(item.outcome === undefined ? {} : { outcome: item.outcome as NonNullable<NudgeEvent["outcome"]> }),
  };
}

export function normalizeSyncLedger(value: unknown): LocalLedger | null {
  let serialized: string;
  try { serialized = JSON.stringify(value); } catch { return null; }
  if (new TextEncoder().encode(serialized).byteLength > maxSerializedBytes) return null;
  const item = record(value);
  if (!item || item.version !== 2 || !Array.isArray(item.completed) || item.completed.length > 2_500 || !Array.isArray(item.tags) || item.tags.length > 100 || !Array.isArray(item.nudgeEvents) || item.nudgeEvents.length > 500) return null;
  const running = item.running === null ? null : timer(item.running);
  const completed = item.completed.map(completedTimer);
  const tags = item.tags.map(tag);
  const nudgePolicy = policy(item.nudgePolicy);
  const nudgeEvents = item.nudgeEvents.map(nudgeEvent);
  if ((item.running !== null && !running) || completed.some((entry) => !entry) || tags.some((entry) => !entry) || !nudgePolicy || nudgeEvents.some((entry) => !entry)) return null;
  return {
    version: 2,
    running,
    completed: completed as CompletedTimer[],
    tags: tags as TagDefinition[],
    nudgePolicy,
    nudgeEvents: nudgeEvents as NudgeEvent[],
  };
}

export function parseSyncCommand(value: unknown): LedgerSyncCommand | null {
  const item = record(value);
  if (
    !item || !string(item.commandId, 200, identifier) || !string(item.deviceId, 200, identifier) ||
    typeof item.baseRevision !== "number" || !Number.isSafeInteger(item.baseRevision) || item.baseRevision < 0 ||
    !string(item.issuedAt, 40) || !Number.isFinite(Date.parse(item.issuedAt))
  ) return null;
  const ledger = normalizeSyncLedger(item.ledger);
  return ledger ? {
    commandId: item.commandId,
    deviceId: item.deviceId,
    baseRevision: item.baseRevision,
    issuedAt: new Date(item.issuedAt).toISOString(),
    ledger,
  } : null;
}
